<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { MessageSquare } from 'lucide-svelte';
  import { ticketsApi } from '$lib/api/tickets';
  import { getErrorMessage } from '$lib/api/client';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import { pretty, ticketKind } from '$lib/utils/format';
  import Avatar from '$lib/components/ui/Avatar.svelte';
  import Chip from '$lib/components/ui/Chip.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import SrcBadge from '$lib/components/tickets/SrcBadge.svelte';
  import SlaChip from '$lib/components/tickets/SlaChip.svelte';
  import type { AssignableUser } from '$lib/types';

  const { projectId, members }: { projectId: number; members: AssignableUser[] } = $props();
  const admin = $derived(session.isAdmin);

  const ticketsQuery = createQuery(() => ({
    queryKey: ['project-tickets', projectId],
    queryFn: () => ticketsApi.forProject(projectId)
  }));

  function assigneeName(id: number | null): string | null {
    if (!id) return null;
    return members.find((m) => m.id === id)?.name ?? null;
  }
</script>

{#if ticketsQuery.isLoading}
  <p style="color:var(--t3);font-size:13.5px">Loading tickets…</p>
{:else if ticketsQuery.isError}
  <p style="color:var(--danger);font-size:13.5px">{getErrorMessage(ticketsQuery.error)}</p>
{:else if ticketsQuery.data?.results.length}
  <div class="list">
    {#each ticketsQuery.data.results as t (t.id)}
      {@const aName = assigneeName(t.assignee_user_id)}
      <div class="card row-card" onclick={() => route.push({ tab: 'ticket', ticketId: t.id, back: route.current })} role="button" tabindex="0" onkeydown={(e) => e.key === 'Enter' && route.push({ tab: 'ticket', ticketId: t.id, back: route.current })}>
        <div class="rc-top">
          <div style="flex:1;min-width:0">
            <div class="rc-title">{t.title}</div>
            <div style="margin-top:9px;display:flex;gap:8px;align-items:center;flex-wrap:wrap">
              <SrcBadge ticket={t} redact={!admin} />
            </div>
          </div>
          <div style="display:flex;flex-direction:column;align-items:flex-end;gap:8px">
            <div style="display:flex;gap:7px;align-items:center">
              <SlaChip status={t.status} createdAt={t.created_at} />
              <Chip label={pretty(t.status)} kind={ticketKind(t.status)} dot />
            </div>
            {#if t.assignee_user_id && aName}
              <Avatar id={t.assignee_user_id} name={aName} size={26} />
            {:else}
              <span style="font-size:12px;color:var(--t3)">Unassigned</span>
            {/if}
          </div>
        </div>
      </div>
    {/each}
  </div>
{:else}
  <EmptyState
    icon={MessageSquare}
    heading={admin ? 'No tickets yet' : 'No tickets assigned'}
    sub={admin
      ? "Triage tickets from the inbox onto this project and they'll show up here."
      : 'When the admin assigns a ticket to this project, it lands here.'}
  />
{/if}
